import type { SelectChangeEvent } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useAccount } from '../../../contexts/AccountContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { getActivityData, getUsers } from '../../../services/api';
import { PERMISSIONS } from '../../../shared/constants/permissions';
import type { UserRole } from '../../../types/auth';
import type {
  ActivityData,
  ActivityFilters,
  ActivityResponse,
  ActivitySummary,
  ActivityType,
  ChartData,
  LoginAnalytics,
  RecentActivity,
} from './types';

const EMPTY_OBJECT: Record<string, never> = {};
const EMPTY_ARRAY: never[] = [];

export const useActivityDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState<ActivityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [usersList, setUsersList] = useState<{ label: string; id: string }[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<RecentActivity | null>(null);

  const [filters, setFilters] = useState<ActivityFilters>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0] as string,
    endDate: new Date().toISOString().split('T')[0] as string,
    activityType: '',
    targetUserRole: '',
    userId: '',
    view: 'dashboard',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const { showSnackbar } = useSnackbar();
  const { account, hasPermission } = useAccount();

  useEffect(() => {
    const fetchUsersList = async () => {
      try {
        const response = await getUsers({ limit: 1000 });
        if (response.users) {
          setUsersList(
            response.users.map((u) => ({
              label: `${u.displayName || 'Unknown'} (${u.email})`,
              id: u.uid,
            }))
          );
        }
      } catch {
        // Silent failure for filter options
      }
    };
    if (hasPermission(PERMISSIONS.USER_VIEW)) {
      fetchUsersList();
    }
  }, [hasPermission]);

  useEffect(() => {
    if (account?.timezone) {
      setFilters((prev) => ({ ...prev, timezone: account.timezone || null }));
    }
  }, [account?.timezone]);

  const loadActivityData = useCallback(async () => {
    setLoading(true);
    try {
      const viewMap: Record<string, string> = {
        overview: 'dashboard',
        recent: 'recent',
        users: 'users',
        logins: 'logins',
      };
      const view = viewMap[activeTab] || 'dashboard';

      const serviceFilters = {
        activityType: filters.activityType || '',
        startDate: filters.startDate ? new Date(filters.startDate) : new Date(),
        endDate: filters.endDate ? new Date(filters.endDate) : new Date(),
        targetUserRole: filters.targetUserRole || '',
        userId: filters.userId || '',
        view: view as 'summary' | 'recent' | 'users' | 'logins' | 'dashboard',
        timezone: filters.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      const response = await getActivityData(serviceFilters);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch activity data');
      }

      const mappedData: ActivityData = {};

      if (response.data?.data?.summary) {
        mappedData.summary = response.data.data.summary as unknown as ActivitySummary;
        setTotalCount(mappedData.summary.totalActivities);
      }
      if (response.data?.data?.loginAnalytics) {
        mappedData.loginAnalytics = response.data.data.loginAnalytics as unknown as LoginAnalytics;
      }
      if (response.data?.data?.topUsers) {
        mappedData.topUsers = (response.data.data.topUsers || EMPTY_ARRAY).map((u) => ({
          userId: u.userId,
          userEmail: u.userEmail,
          userRole: (u.userRole as UserRole) || 'user',
          activityCount: u.activityCount,
          lastActivity: u.lastActivity || '',
        }));
      }
      if (response.data?.data?.recentActivities) {
        mappedData.recentActivities = (response.data.data.recentActivities || EMPTY_ARRAY).map(
          (a) => {
            const result: RecentActivity = {
              id: a.id,
              activityType: a.activityType as ActivityType,
              userRole: (a.userRole as UserRole) || 'user',
              timestamp: a.timestamp,
              data: a.data || EMPTY_OBJECT,
            };
            if (a.userId) {
              result.userId = a.userId;
            }
            return result;
          }
        );
      }

      const activityResponse: ActivityResponse = {
        scope: response.data?.scope === 'platform' ? 'platform' : 'organization',
        filters: {
          startDate: filters.startDate,
          endDate: filters.endDate,
        },
        data: mappedData,
      };

      setData(activityResponse);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showSnackbar(`Failed to load activity data: ${message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showSnackbar, page, rowsPerPage, activeTab]);

  useEffect(() => {
    loadActivityData();
  }, [loadActivityData]);

  const handleFilterChange = (field: keyof ActivityFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange =
    (field: keyof ActivityFilters) => (event: SelectChangeEvent<string>) => {
      handleFilterChange(field, event.target.value);
    };

  const processLoginTimestamps = (
    timestamps: string[]
  ): { hourly: number[]; daily: Record<string, number> } => {
    const hourly = Array(24).fill(0);
    const daily: Record<string, number> = {};

    for (const isoString of timestamps) {
      try {
        const date = new Date(isoString);
        hourly[date.getHours()]++;
        const dateKey = date.toLocaleDateString('fr-CA');
        daily[dateKey] = (daily[dateKey] || 0) + 1;
      } catch {
        // Skip invalid date strings
      }
    }

    return { hourly, daily };
  };

  const prepareChartData = (): ChartData => {
    const emptyData: ChartData = {
      dailyData: [],
      activityTypeData: [],
      hourlyLoginData: [],
      dailyLoginData: [],
    };

    if (!data?.data) {
      return emptyData;
    }

    const { summary, loginAnalytics } = data.data;

    const dailyData = summary?.dailyBreakdown
      ? Object.entries(summary.dailyBreakdown)
          .map(([date, count]) => ({ date, activities: count }))
          .sort((a, b) => a.date.localeCompare(b.date))
      : [];

    const activityTypeData = summary?.activityBreakdown
      ? Object.entries(summary.activityBreakdown).map(([type, count]) => ({
          id: type,
          label: type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          value: count,
        }))
      : [];

    const { hourly, daily } = loginAnalytics?.loginTimestamps
      ? processLoginTimestamps(loginAnalytics.loginTimestamps)
      : { hourly: Array(24).fill(0), daily: {} };

    const hourlyLoginData = hourly.map((logins, hour) => ({
      hour: `${hour}:00`,
      logins,
    }));

    const dailyLoginData = Object.entries(daily)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return { dailyData, activityTypeData, hourlyLoginData, dailyLoginData };
  };

  const handleViewDetails = (activity: RecentActivity) => {
    setSelectedActivity(activity);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedActivity(null);
  };

  const getUserLabel = (userId?: string) => {
    if (!userId) {
      return 'Anonymous';
    }
    const user = usersList.find((u) => u.id === userId);
    return user ? user.label : userId;
  };

  const formatTimestamp = (timestamp: string): string => {
    if (!timestamp) {
      return 'N/A';
    }
    try {
      const dateObj = new Date(timestamp);
      return dateObj.toLocaleString();
    } catch {
      return 'Invalid Date';
    }
  };

  const getActivityTypeColor = (
    activityType: string
  ): 'success' | 'info' | 'warning' | 'secondary' | 'primary' | 'error' | 'default' => {
    switch (activityType) {
      case 'login':
        return 'success';
      case 'logout':
        return 'info';
      case 'role_change':
        return 'warning';
      case 'helpdesk_action':
        return 'secondary';
      case 'tender_created':
        return 'primary';
      case 'document_uploaded':
        return 'info';
      case 'document_regenerated':
        return 'secondary';
      case 'document_reviewed':
        return 'secondary';
      case 'ai_service_used':
        return 'secondary';
      case 'error_occurred':
        return 'error';
      case 'security_event':
        return 'error';
      default:
        return 'default';
    }
  };

  return {
    activeTab,
    setActiveTab,
    data,
    loading,
    usersList,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
    detailDialogOpen,
    selectedActivity,
    filters,
    handleFilterChange,
    handleSelectChange,
    loadActivityData,
    prepareChartData,
    handleViewDetails,
    handleCloseDetailDialog,
    getUserLabel,
    formatTimestamp,
    getActivityTypeColor,
  };
};
